
import React, { Suspense, useEffect, useLayoutEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AuthService from "src/_services/AuthService";
import StyleWrapper from "src/StyleWrapper";
import ReactGA from "react-ga4";
import "./style.css";
import SocketContextProvider from "src/_contexts/socket";
import { useDispatch, useSelector } from "react-redux";
import { getCurrentUserProfile } from "src/_store/entities/profile"
import { setCredits, setSubscriptionInfo } from "src/_store/entities/creditsAndSubscription";
import { Box, CircularProgress } from "@mui/material";
import { updateLoading } from "src/_store/entities/profile";
import Hotjar from '@hotjar/browser';
import { HOTJAR_SETTINGS, ROLES } from "./constants";
import { useGetAllCitiesQuery, useGetWalletBalanceQuery } from "./_api/mapitShipApi";
import { useGetSubscriptionInfoQuery } from "./_api/planApi";
import MapitRoutes from "./_router/router";
import PaymentStatusDialog from "./_modules/MapitShip/views/AccountBalance/components/PaymentStatusDialog";
import InstallAppPrompt from "src/components/common/InstallAppPrompt";
import AppUpdatePrompt from "src/components/common/AppUpdatePrompt";
import { AiChatLauncher } from "src/_modules/AiSupportChat";
const trackers = [
  {
    trackingId: "G-PM7ESEM6BP",
    gaOptions: { name: "tracker1" },
  },
  {
    trackingId: "UA-244051607-1",
    gaOptions: { name: "tracker2" },
  },
];

const LoadingComponent = () => (
  <Box sx={{ display: 'flex', justifyContent: "center", alignItems: "center", height: "100vh" }}>
    <CircularProgress color="primary" size={80} />
  </Box>
)

const App = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { profile, loading, errorStatus } = useSelector((state) => state.entities.profile)

  const isMerchant = !!profile && [ROLES.merchant, ROLES.shippingCompany].includes(profile?.merchant?.role);

  const { data: credits } = useGetWalletBalanceQuery({}, { skip: isMerchant ? false : true })
  const { data: subscriptionInfo } = useGetSubscriptionInfoQuery({}, { skip: isMerchant ? false : true })
  const { data: cities } = useGetAllCitiesQuery({}, { skip: !!profile ? false : true });

  useEffect(() => {
    if (!!credits) {
      dispatch(setCredits(credits?.balance))
    }
  }, [credits])

  useEffect(() => {
    if (!!subscriptionInfo) {
      dispatch(setSubscriptionInfo(subscriptionInfo))
    }
  }, [subscriptionInfo])

  let currentUser = AuthService.getCurrentUser();

  if (currentUser?.role === "merchant" || currentUser?.role === "admin") {
    localStorage.clear();
    currentUser = AuthService.getCurrentUser();
  }

  const isTokenExist = localStorage.getItem("token")
  const isProd = process.env.REACT_APP_ENV === "production"

  useEffect(() => {
    if (isProd) {
      ReactGA.initialize(trackers);
      ReactGA.send(window.location.pathname + window.location.search);
      Hotjar.init(HOTJAR_SETTINGS.siteId, HOTJAR_SETTINGS.hotjarVersion);
    }
  }, [])

  useLayoutEffect(() => {
    if (isTokenExist) {
      dispatch(getCurrentUserProfile())
    } else {
      dispatch(updateLoading(false))
    }
  }, [])

  useEffect(() => {
    if (errorStatus === 600 || errorStatus === 402) {
      navigate("/")
      localStorage.clear()
      dispatch(updateLoading(false))
    } else if (errorStatus === "UNKNOWN") {
      navigate("/errors/network")
      dispatch(updateLoading(false))
    }
  }, [errorStatus])

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      const isAdAppear = localStorage.getItem("isAdAppear");
      if (isAdAppear === "OPEN") {
        localStorage.setItem("isAdAppear", 'CLOSE');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Payment Status Dialog 
  const [searchParams] = useSearchParams()

  const [open, setOpen] = useState(false);

  const target = searchParams.get('target')
  const payment_status = searchParams.get('payment_status')
  const message = searchParams.get('message')

  useEffect(() => {
    if (!!payment_status) {
      setOpen(true);
    }
  }, [target, payment_status]);

  return (
    <StyleWrapper>
      <SocketContextProvider currentUser={profile}>
        <PaymentStatusDialog
          open={open}
          onClose={() => setOpen(false)}
          payment_status={payment_status}
          message={message}
        />
        {/* Install banner: a real prompt button on Android, Add-to-Home-Screen steps on iOS */}
        <InstallAppPrompt />
        <AiChatLauncher />
        <AppUpdatePrompt />
        <Suspense fallback={<LoadingComponent />}>
            {loading ? <LoadingComponent /> : <MapitRoutes currentUser={profile} />}
          </Suspense>
      </SocketContextProvider>
    </StyleWrapper>
  );
};

export default React.memo(App);
